import { useState, type ReactNode } from "react";
import {
  Button,
  ContactUsModal,
  type ContactMessagePrefill,
  type ContactTopic,
  type RequestCallPrefill,
} from "@dearbill/shared";

/** Partner support topics, in plain English because the partner SPA has no i18n. */
const PARTNER_TOPICS: ContactTopic[] = [
  { value: "Referrals & members", label: "Referrals & members" },
  { value: "Billing & payments", label: "Billing & payments" },
  { value: "Account & access", label: "Account & access" },
  { value: "Technical problem", label: "Technical problem" },
  { value: "Something else", label: "Something else" },
];

interface ContactSupportButtonProps {
  /** Message-tab email/phone prefill (authenticated TopBar); omit on the login screen. */
  prefill?: ContactMessagePrefill | undefined;
  /** Partner-session bearer; omit on the login screen. `/contact` is public. */
  token?: string | undefined;
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
  label?: string;
  /** Extra classes forwarded to the trigger `Button` (e.g. inline footer styling). */
  className?: string;
}

/**
 * "Contact us" trigger for the partner portal, with the modal's open state.
 *
 * The authenticated `TopBar` passes `prefill` and `token`. The login screen
 * passes neither, so a locked-out partner can still reach support. The shared
 * two-tab `ContactUsModal` supplies the English defaults. The call tab prefills
 * the phone when one is on file. Partner `me` carries a single `name` string,
 * so the call tab's name fields stay blank rather than splitting it by guesswork.
 */
export function ContactSupportButton({
  prefill,
  token,
  variant = "ghost",
  size = "sm",
  label = "Contact us",
  className,
}: ContactSupportButtonProps): ReactNode {
  const [open, setOpen] = useState(false);

  const callPrefill: RequestCallPrefill | undefined = prefill?.phone
    ? { phone: prefill.phone }
    : undefined;

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <ContactUsModal
        open={open}
        onClose={() => setOpen(false)}
        source="partner"
        baseUrl={import.meta.env.VITE_API_BASE_URL ?? ""}
        topics={PARTNER_TOPICS}
        {...(callPrefill ? { callPrefill } : {})}
        {...(prefill ? { messagePrefill: prefill } : {})}
        {...(token ? { token } : {})}
      />
    </>
  );
}
